import { clearPendingSignMessage, getPendingSignMessage, isMobileDevice } from '@/lib/mobileWalletUtils';
import { getPhantomSession, getPhantomSharedSecret, parsePhantomSignMessageResponse, processPhantomSignMessageResponse } from '@/lib/phantomMobileEncryption';
import { useCallback, useEffect, useState } from 'react';
import { usePostPrimaryWallet, usePrimaryWallet } from './useWalletQueries';
import { PublicKey } from '@solana/web3.js';
import { verifySignature } from '@/lib/messageSigningUtils';
import { toast } from './use-toast';

const useWalletMobilePhantom = () => {
    const [isConnecting, setIsConnectingPhantom] = useState<boolean>(() => localStorage.getItem('IS_CONNECTING_PHANTOM') === 'true');
    const { data: primaryWalletData } = usePrimaryWallet();
    const primaryWallet = primaryWalletData?.wallet;
    const { mutateAsync: setPrimaryWalletAsync } = usePostPrimaryWallet();
    
    // Handle mobile Phantom sign message response
    useEffect(() => {
      const handlePhantomSignMessageResponse = async () => {
          // Only process on mobile
          if (!isMobileDevice()) return;

          const pendingSignMessage = getPendingSignMessage();
          const signResponse = parsePhantomSignMessageResponse();

          // Only proceed if we have BOTH pending data AND a response in the URL
          // This prevents errors when just opening the component normally
          if (!pendingSignMessage || !signResponse) return;

          try {
              // Check for error response
              if (signResponse.errorCode || signResponse.errorMessage) {
                  throw new Error(signResponse.errorMessage || 'User rejected signature request');
              }

              // Process the encrypted signature response
              const { signature } = processPhantomSignMessageResponse(signResponse);

              // Verify the signature
              const walletPublicKey = new PublicKey(pendingSignMessage.walletAddress);
              const isValid = verifySignature(
                  pendingSignMessage.message,
                  signature,
                  walletPublicKey
              );

              if (!isValid) {
                  throw new Error('Signature verification failed');
              }

              // Signature is valid, set as primary
              await setPrimaryWalletAsync(pendingSignMessage.walletId);

              toast({
                  title: 'Primary Wallet Set',
                  description: 'Wallet ownership verified and set as primary.',
              });
          } catch (error: any) {
              toast({
                  title: 'Verification Failed',
                  description: error.message || 'Failed to verify wallet ownership',
                  variant: 'destructive',
              });
          } finally {
              // Always clean up pending data and URL params
              clearPendingSignMessage();
              localStorage.removeItem('wallet_details_auto_open');

              // Clean URL parameters
              const url = new URL(window.location.href);
              url.searchParams.delete('data');
              url.searchParams.delete('nonce');
              url.searchParams.delete('errorCode');
              url.searchParams.delete('errorMessage');
              window.history.replaceState({}, document.title, url.toString());
          }
      };

      handlePhantomSignMessageResponse();
  }, []);

    function setIsConnecting(isConnecting: boolean) {
        if (isConnecting) {
            setIsConnectingPhantom(isConnecting);
            localStorage.setItem('IS_CONNECTING_PHANTOM', isConnecting.toString());
        } else {
            setIsConnectingPhantom(isConnecting);
            localStorage.removeItem('IS_CONNECTING_PHANTOM');
        }
    }

    const hasValidPhantomSession = useCallback((): boolean => {
        try {
          // Use the existing helper functions that check localStorage with correct keys
          const session = getPhantomSession();
          const sharedSecret = getPhantomSharedSecret();

          // Both session and shared secret must exist for a valid connection
          return !!(session && sharedSecret) && isMobileDevice() && primaryWallet?.walletAdapter?.toLowerCase() === 'phantom';
        } catch {
          return false;
        }
      }, [primaryWallet?.walletAdapter]);

    return {
        isConnecting,
        setIsConnecting,
        hasValidPhantomSession,
    }
}

export default useWalletMobilePhantom;
